import { ofType } from 'redux-observable';
import { Observable, merge, of, EMPTY, BehaviorSubject, Subject, from } from 'rxjs';
import { mergeMap, catchError, take, switchMap, takeWhile, map, takeUntil } from 'rxjs/operators';

import { ModalService } from 'ui-components/services/modal-service';

import * as Actions from './batches.purchase.actions';
import * as BatchPurchasesHelper from './batches.purchase.helpers';
import {
  BATCH_JOB_ENTITY_GROUP_NAME,
  BATCH_JOB_ENTITIY_ID_ATTRIBUTE,
  BATCH_ENTITY_GROUP_NAME,
  BATCH_ENTITY_ID_ATTRIBUTE,
  BATCH_JOB_STATUS_COMPLETED,
  BATCH_JOB_STATUS_ERRORED,
} from './constants';
import { normalizeDataArray, buildEntityPayload, buildJobData } from './data-helpers';
import { FeatureFlags } from '../../../feature-flags/feature-flags';
import { HAS_ACCEPTED_UPS_LABEL_TERMS_SETTING } from '../../../wallet/utilities/constants';
import { LabelBatchStatusStep } from '../../../enums/label-batch-status-step';
import { UserSettings } from '../../../enums/user-settings/user-settings';
import { UserSettingsOptions } from '../../../enums/user-settings/user-settings-options';
import { getSummary } from '../../../fulfillment-planner/rate-recommendation/rate-recommendations.api';
import { InsuranceTermsService, hasUserAcceptedInsuranceTerms } from '../../../services/insurance-terms-service';
import { LogService } from '../../../services/log-service';
import * as PrintActions from '../../../store/batches/print/batches.print.actions';
import * as EntitiesActions from '../../../store/entities/entities.actions';
import Global from '../../../store/global-selectors';
import * as PostagePurchaseActions from '../../../store/postage-purchase/postage-purchase.actions';
import { COST_REVIEW_TIMEOUT_ERROR } from '../../bulk/bulk-error-types';
import { startCostReviewGetMessages } from '../../cost-review';
import { determineShouldShowCostReviewByCostReviewBalances } from '../../quickship/quickship.helpers';
import { bulkApplyRecommendationsFromLabelPurchaseCostReview } from '../../recommendations/recommendations.observables';
import * as Api from '../grid/batches.grid.api';
import { getIsBulkRateShopperRecommendationsEnabled } from '../../../hooks/use-is-bulk-rate-shopper-recommendations-enabled';

const prepareBatchCostReviewSummary = (fulfillmentPlanSetId, jobGuids, getState, recommendationSummary, appliedSavings) => {
  return new Observable((observer) => {
    const state = getState();
    const costReviewSummary = BatchPurchasesHelper.getAggregatedCostReviewData(state, jobGuids);
    const hasInsurance = (costReviewSummary?.insuranceCosts || []).length !== 0;
    // NOTE: At time of writing, insuranceCosts will only contain no more than one insurance provider.
    // This will need to be updated whenever a seller can combine multiple insurance providers in the same purchase
    const insuranceProviderId = costReviewSummary?.insuranceCosts?.[0]?.insuranceProviderId;

    const openCostReview = () => {
      observer.next(Actions.openBatchesCostReviewModal(fulfillmentPlanSetId, costReviewSummary, recommendationSummary, appliedSavings));
      observer.complete();
    };

    if (hasUserAcceptedInsuranceTerms(state, insuranceProviderId) || !hasInsurance) {
      openCostReview();
      return;
    }

    const onInsuranceTermsModalAccept = openCostReview;

    const onInsuranceTermsModalClose = () => {
      const lbsModalId = Global.selectBatchesPurchaseLabelBatchStatusModalId(state);
      ModalService.closeModalById(lbsModalId);
      observer.complete();
    };

    InsuranceTermsService.launchInsuranceTermsModal(onInsuranceTermsModalAccept, onInsuranceTermsModalClose, insuranceProviderId);
  });
};

const setCostReviewData = (fulfillmentPlanSetId, jobGuids, getState, appliedSavings) => {
  const state = getState();
  const isSwitchAndSaveRecommendationsEnabled = new FeatureFlags({ state }).enableSwitchAndSaveRateRecommendations;
  const isBulkRateShopperRecommendationsEnabled = getIsBulkRateShopperRecommendationsEnabled(state);
  const setCostReviewDataWithRecommendations = (recommendationSummary) =>
    prepareBatchCostReviewSummary(fulfillmentPlanSetId, jobGuids, getState, recommendationSummary, appliedSavings);

  if (!isSwitchAndSaveRecommendationsEnabled) {
    return setCostReviewDataWithRecommendations();
  }

  // don't get recommendations again if user already applied some
  if (appliedSavings?.value) {
    return setCostReviewDataWithRecommendations();
  }

  return from(getSummary({ fulfillmentPlanSetId, isRateShopperRecommendationsEnabled: isBulkRateShopperRecommendationsEnabled })).pipe(
    mergeMap((response) => setCostReviewDataWithRecommendations(response.data)),
    catchError(() => setCostReviewDataWithRecommendations()),
  );
};

const processCostReviewMessage = ({ action$, costReviewData, getState, jobGuid, quickshipProps, appliedSavings }) => {
  const state = getState();
  const fulfillmentPlanSetId = Global.selectBatchesPurchaseFufillmentPlanSetIdByJobId(state, {
    jobId: jobGuid,
  });
  const batchEntity = Global.selectEntitiesBatchByFulfillmentPlanSetId(state, {
    fulfillmentPlanSetId,
  });
  const normalizedBatches = normalizeDataArray(BATCH_ENTITY_GROUP_NAME, BATCH_ENTITY_ID_ATTRIBUTE, [{ ...batchEntity, error: undefined }]);

  const jobHasError = !!costReviewData.errorMessage;
  const updatedJob = {
    ...Global.selectEntitiesBatchJobByJobId(state, { jobId: jobGuid }),
    costReviewData,
    status: jobHasError ? BATCH_JOB_STATUS_ERRORED : BATCH_JOB_STATUS_COMPLETED,
  };
  const payload = normalizeDataArray(BATCH_JOB_ENTITY_GROUP_NAME, BATCH_JOB_ENTITIY_ID_ATTRIBUTE, [updatedJob]);
  const entities = buildEntityPayload({ ...payload, ...normalizedBatches });
  const updateJob$ = of(EntitiesActions.entitiesLoad(entities));

  // quickship skip cost summary
  const userQuickshipShowCostSummarySetting = quickshipProps.userQuickshipSettings.find(
    (setting) => setting.settingName === UserSettings.quickshipShowCostSummary.settingName,
  ) || {
    settingValue: UserSettingsOptions.quickshipNeverShowCostSummary.settingValue,
  };
  const { enableUpsTerms2 } = new FeatureFlags({ state });
  const labelProviders = Global.selectSellerLabelProviders(state);
  if (
    !determineShouldShowCostReviewByCostReviewBalances(costReviewData, {
      ...quickshipProps,
      isUpsTermsEnabled: enableUpsTerms2,
      labelProviders,
    }) ||
    (jobHasError && userQuickshipShowCostSummarySetting?.settingValue === UserSettingsOptions.quickshipNeverShowCostSummary.settingValue)
  ) {
    return merge(
      action$.pipe(
        ofType(EntitiesActions.ENTITIES_LOAD),
        take(1),
        mergeMap(() => {
          return of(PostagePurchaseActions.postageAndInsurancePurchaseSuccess(jobGuid));
        }),
      ),
      updateJob$,
    );
  }

  if (jobHasError) {
    return updateJob$;
  }

  return merge(
    action$.pipe(
      ofType(EntitiesActions.ENTITIES_LOAD),
      take(1),
      mergeMap(() => {
        return setCostReviewData(fulfillmentPlanSetId, [jobGuid], getState, appliedSavings);
      }),
    ),
    updateJob$,
  );
};

const onCostReviewFailure = (getState, fulfillmentPlanSetId) => {
  const batchEntityToUpdate = Global.selectEntitiesBatchByFulfillmentPlanSetId(getState(), {
    fulfillmentPlanSetId,
  });
  const copiedEntity = { ...batchEntityToUpdate };
  copiedEntity.error = COST_REVIEW_TIMEOUT_ERROR;
  const errorEntities = buildEntityPayload(normalizeDataArray(BATCH_ENTITY_GROUP_NAME, BATCH_ENTITY_ID_ATTRIBUTE, [copiedEntity]));
  return EntitiesActions.entitiesLoad(errorEntities);
};

export const startBatchPurchaseCostReview = ({
  action$,
  getState,
  fulfillmentPlanSetId,
  quickshipProps,
  pusherTimeoutMs = 10000,
  appliedSavings,
}) => {
  const state = getState();
  const batchEntity = Global.selectEntitiesBatchByFulfillmentPlanSetId(state, {
    fulfillmentPlanSetId,
  });

  const { excluded, shouldPrint } = batchEntity;

  const costReviewTimeout$ = new Subject();
  const onFailure = () => {
    costReviewTimeout$.next(onCostReviewFailure(getState, fulfillmentPlanSetId));
  };
  const startCostReviewSubject = new BehaviorSubject(false);
  return startCostReviewSubject
    .pipe(
      mergeMap((isRetry) => {
        const logContext = {
          fulfillmentPlanSetId,
          purchasePoint: 'batch',
        };

        const retryOrConcludeFailure = ({ jobGuids, errorOnMessage }) => {
          if (!errorOnMessage) {
            LogService.logInfo(
              !isRetry
                ? 'Timeout waiting for Cost Review pusher message. Automatically retrying...'
                : 'Timeout waiting for Cost Review pusher message (retry). Showing error to user.',
              {
                ...logContext,
                jobGuids,
              },
            );
          }
          if (!isRetry) {
            startCostReviewSubject.next(true);
            return;
          }
          onFailure();
        };

        const hasAcceptedUpsLabelTerms =
          Global.selectSellerSellerSetting(getState(), { settingName: HAS_ACCEPTED_UPS_LABEL_TERMS_SETTING }) === 'true';

        return startCostReviewGetMessages({
          apiCallback: () => Api.getCostReviewForBatch(fulfillmentPlanSetId, excluded, hasAcceptedUpsLabelTerms),
          pusherTimeoutMs,
          onError: retryOrConcludeFailure,
          logContext,
        }).pipe(
          switchMap(({ jobGuids, findMessage$ }) => {
            // jobGuids is an array but will only ever contain at most 1 jobGuid
            if (jobGuids.length === 0) {
              return shouldPrint ? of(PrintActions.startLabelPrintedCheck([fulfillmentPlanSetId])) : EMPTY; // we might need some notification here of a problem, and either use error case or end.
            }
            const [theOneGuid] = jobGuids;
            // we need to concat jobguids together from previous attempts at cost review
            const latestBatchEntity = Global.selectEntitiesBatchByFulfillmentPlanSetId(state, {
              fulfillmentPlanSetId,
            });
            const batchEntityToUpdate = {
              ...batchEntity,
              costReviewJobIds: [...(latestBatchEntity?.costReviewJobIds || []), ...jobGuids],
              step: LabelBatchStatusStep.CostReview.ordinal,
            };

            const normalizedBatches = normalizeDataArray(BATCH_ENTITY_GROUP_NAME, BATCH_ENTITY_ID_ATTRIBUTE, [batchEntityToUpdate]);

            const jobData = buildJobData(jobGuids, fulfillmentPlanSetId);
            const { jobs, jobIdsToFpSetIdMap } = jobData;

            const normalizedJobs = normalizeDataArray(BATCH_JOB_ENTITY_GROUP_NAME, BATCH_JOB_ENTITIY_ID_ATTRIBUTE, jobs);

            const entities = buildEntityPayload({
              ...normalizedBatches,
              ...normalizedJobs,
            });

            const next$ = of(
              Actions.costReviewBatchesSuccess(fulfillmentPlanSetId, jobIdsToFpSetIdMap),
              EntitiesActions.entitiesLoad(entities),
            );

            return merge(
              isRetry ? costReviewTimeout$ : EMPTY,
              next$,
              findMessage$.pipe(
                takeUntil(costReviewTimeout$),
                take(1),
                map(({ pusherData }) => {
                  return {
                    pusherData,
                    theOneGuid,
                  };
                }),
              ),
            );
          }),
        );
      }),
      takeWhile((next) => {
        return !next?.pusherData;
      }, true),
      switchMap((next) => {
        if (!next?.pusherData) {
          return of(next);
        }
        const { pusherData, theOneGuid } = next;

        return processCostReviewMessage({
          action$,
          costReviewData: pusherData,
          getState,
          jobGuid: theOneGuid,
          quickshipProps,
          appliedSavings,
        });
      }),
    )
    .pipe(
      catchError(() => {
        // TODO: handle error case
        return EMPTY;
      }),
    );
};

export const waitForEndOfCostReview = (action$) => {
  return action$.pipe(ofType(PostagePurchaseActions.POSTAGE_AND_INSURANCE_PURCHASE_SUCCESS), take(1));
};

export const batchCostReviewApplyRecommendations = (action$, getState, onSuccess) => {
  return action$.pipe(
    ofType(Actions.BATCH_COST_REVIEW_APPLY_RECOMMENDATIONS),
    mergeMap(
      ({
        fulfillmentPlanSetId,
        bulkUpdateJobGuid,
        serviceFilters,
        insuranceFilters,
        rateShopperFilters,
        isRateShopperRecommendationsEnabled,
      }) => {
        const request = {
          bulkUpdateJobGuid,
          serviceFilters,
          insuranceFilters,
          rateShopperFilters,
          isRateShopperRecommendationsEnabled,
        };

        const handleSuccess = (amountSaved) => {
          return merge(onSuccess(amountSaved), of(Actions.batchCostReviewApplyRecommendationsSuccess(fulfillmentPlanSetId)));
        };

        const handleError = () => {
          return of(Actions.batchCostReviewApplyRecommendationsError(fulfillmentPlanSetId));
        };

        return bulkApplyRecommendationsFromLabelPurchaseCostReview(getState, request, handleSuccess, handleError);
      },
    ),
  );
};
